import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MapPin, Store, Loader2 } from "lucide-react";
import MapAddressPicker from "@/components/MapAddressPicker";

export default function CheckoutPage() {
  const { cart, user, token, clearCart, estimateCartTotal, t } = useApp();
  const navigate = useNavigate();
  const [orderType, setOrderType] = useState("pickup");
  const [address, setAddress] = useState(null);
  const [saveAddressDialogOpen, setSaveAddressDialogOpen] = useState(false);
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [pendingDeliveryAddress, setPendingDeliveryAddress] = useState(null);
  const [savingAddressAfterOrder, setSavingAddressAfterOrder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calculatedCart, setCalculatedCart] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoAppliedCode, setPromoAppliedCode] = useState("");
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [unavailableProductIds, setUnavailableProductIds] = useState(new Set());
  const [addressMode, setAddressMode] = useState(null); // null | "saved" | "new"
  const [selectedSavedAddress, setSelectedSavedAddress] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [defaultAddressId, setDefaultAddressId] = useState(null);
  const [restaurantInfo, setRestaurantInfo] = useState({
    accepting_pickup: true,
    accepting_delivery: true,
    is_open: true,
    delivery_fee: 49,
    min_order_amount: 0,
    latitude: null,
    longitude: null,
    address: "",
  });

  useEffect(() => {
    if (orderPlaced) return;
    if (!token) {
      navigate("/auth", { state: { from: "/checkout" } });
      return;
    }
    if (cart.length === 0) {
      navigate("/");
      return;
    }
  }, [token, cart, navigate, orderPlaced]);

  useEffect(() => {
    const calc = async () => {
      if (cart.length === 0) return;
      setCalculating(true);
      try {
        const items = cart.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
          removed_ingredients: item.removedIngredients || [],
          modifiers: (item.modifiers || []).map(m => ({ modifier_id: m.modifierId, quantity: m.quantity }))
        }));
        const res = await api.post("/menu/calculate-cart", {
          items,
          order_type: orderType,
          promo_code: promoAppliedCode || null,
        });
        setCalculatedCart(res.data);
      } catch (e) {
        if (promoAppliedCode) {
          toast.error(e.response?.data?.detail || t("Invalid promo code"));
          setPromoAppliedCode("");
        } else {
          console.error("Price calculation failed:", e);
        }
      } finally {
        setCalculating(false);
      }
    };
    calc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, orderType, promoAppliedCode]);

  useEffect(() => {
    if (cart.length === 0) {
      setUnavailableProductIds(new Set());
      return;
    }

    let mounted = true;
    const loadAvailability = async () => {
      try {
        const res = await api.get("/menu/products");
        const outIds = new Set(
          (res.data || [])
            .filter((p) => p.out_of_stock)
            .map((p) => p.id)
        );
        if (mounted) setUnavailableProductIds(outIds);
      } catch {
        if (mounted) setUnavailableProductIds(new Set());
      }
    };

    loadAvailability();
    return () => {
      mounted = false;
    };
  }, [cart]);

  useEffect(() => {
    let mounted = true;
    const loadRestaurantInfo = async () => {
      try {
        const res = await api.get("/menu/restaurant-info");
        if (!mounted) return;
        const info = {
          accepting_pickup: res.data?.accepting_pickup ?? true,
          accepting_delivery: res.data?.accepting_delivery ?? true,
          is_open: res.data?.is_open ?? true,
          delivery_fee: Number(res.data?.delivery_fee ?? 49),
          min_order_amount: Number(res.data?.min_order_amount ?? 0),
          latitude: res.data?.latitude ?? null,
          longitude: res.data?.longitude ?? null,
          address: res.data?.address ?? "",
        };
        setRestaurantInfo(info);

        if (orderType === "pickup" && !info.accepting_pickup && info.accepting_delivery) {
          setOrderType("delivery");
        }
        if (orderType === "delivery" && !info.accepting_delivery && info.accepting_pickup) {
          setOrderType("pickup");
        }
      } catch {
        if (mounted) {
          setRestaurantInfo({
            accepting_pickup: true,
            accepting_delivery: true,
            is_open: true,
            delivery_fee: 49,
            min_order_amount: 0,
            latitude: null,
            longitude: null,
            address: "",
          });
        }
      }
    };

    loadRestaurantInfo();
    return () => {
      mounted = false;
    };
  }, [orderType]);

  useEffect(() => {
    if (!token) return;
    let mounted = true;
    api.get("/auth/me", { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!mounted) return;
        setSavedAddresses(res.data?.addresses || []);
        setDefaultAddressId(res.data?.default_address_id || null);
      })
      .catch(() => { if (mounted) { setSavedAddresses([]); setDefaultAddressId(null); } });
    return () => { mounted = false; };
  }, [token]);

  useEffect(() => {
    if (orderType !== "delivery" || addressMode !== null || !defaultAddressId || savedAddresses.length === 0) return;
    const defaultAddr = savedAddresses.find(a => a.id === defaultAddressId);
    if (defaultAddr) {
      setSelectedSavedAddress(defaultAddr);
      setAddress({ street: defaultAddr.street, city: defaultAddr.city, display: `${defaultAddr.street}, ${defaultAddr.city}` });
      setAddressMode("saved");
    }
  }, [orderType, defaultAddressId, savedAddresses, addressMode]);

  const hasUnavailableItems = cart.some((item) => unavailableProductIds.has(item.product?.id));

  const getFallbackItemTotal = (item) => {
    const base = Number(item.product?.base_price || item.base_price || 0);
    const modsTotal = (item.modifiers || []).reduce(
      (sum, mod) => sum + Number(mod.price || 0) * Number(mod.quantity || 1),
      0
    );
    return (base + modsTotal) * Number(item.quantity || 1);
  };

  const subtotalValue = Number(calculatedCart?.subtotal ?? estimateCartTotal());
  const deliveryFeeValue = orderType === "delivery"
    ? Number(calculatedCart?.delivery_fee ?? restaurantInfo.delivery_fee ?? 0)
    : 0;
  const discountValue = Number(calculatedCart?.discount_amount || 0);
  const totalValue = Number(
    calculatedCart?.total ?? Math.max(0, subtotalValue + deliveryFeeValue - discountValue)
  );
  const minOrderAmountValue = Number(calculatedCart?.min_order_amount ?? restaurantInfo.min_order_amount ?? 0);
  const meetsMinOrder = subtotalValue >= minOrderAmountValue;

  const continueToOrderTracking = (orderId) => {
    setOrderPlaced(true);
    clearCart();
    toast.success("Order placed!");
    navigate(`/order/${orderId}`);
  };

  const handlePlaceOrder = async () => {
    if (!restaurantInfo.is_open) {
      toast.error(t("We're closed and not accepting orders."));
      return;
    }
    if (orderType === "pickup" && !restaurantInfo.accepting_pickup) {
      toast.error(t("Pickup temporarily unavailable."));
      return;
    }
    if (orderType === "delivery" && !restaurantInfo.accepting_delivery) {
      toast.error(t("Delivery temporarily unavailable."));
      return;
    }
    if (hasUnavailableItems) {
      toast.error(t("One or more products are out of stock. Remove them to order."));
      return;
    }
    if (!meetsMinOrder) {
      toast.error(`${t("Minimum order amount is")} ${minOrderAmountValue.toFixed(0)} kr.`);
      return;
    }
    if (orderType === "delivery" && !address) {
      toast.error(t("Please enter your delivery address"));
      return;
    }
    if (duplicateAddress) {
      toast.error(t("You already have this address saved"));
      return;
    }

    const deliveryAddressPayload = orderType === "delivery"
      ? {
          street: address.street || address.display,
          city: address.city || "",
        }
      : null;

    setLoading(true);
    try {
      const items = cart.map(item => ({
        product_id: item.product.id,
        quantity: item.quantity,
        removed_ingredients: item.removedIngredients || [],
        modifiers: (item.modifiers || []).map(m => ({ modifier_id: m.modifierId, quantity: m.quantity })),
        selected_options: (item.selectedOptions || []).map(o => ({ group_id: o.groupId, option_id: o.optionId })),
      }));
      const res = await api.post("/orders", {
        items,
        order_type: orderType,
        delivery_address: deliveryAddressPayload,
        special_instructions: specialInstructions || null,
        promo_code: promoAppliedCode || null,
      });

      if (orderType === "delivery" && !user?.addresses?.length && deliveryAddressPayload) {
        setPendingOrderId(res.data.id);
        setPendingDeliveryAddress(deliveryAddressPayload);
        setSaveAddressDialogOpen(true);
        return;
      }

      continueToOrderTracking(res.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Failed to place order"));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAddressAfterOrder = async () => {
    if (!pendingOrderId || !pendingDeliveryAddress) {
      setSaveAddressDialogOpen(false);
      return;
    }

    setSavingAddressAfterOrder(true);
    try {
      await api.post(
        "/auth/addresses",
        {
          label: "Home",
          street: pendingDeliveryAddress.street,
          city: pendingDeliveryAddress.city,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("Address saved to profile"));
    } catch {
      toast.error(t("Order placed, but address could not be saved"));
    } finally {
      const orderId = pendingOrderId;
      setSavingAddressAfterOrder(false);
      setSaveAddressDialogOpen(false);
      setPendingOrderId(null);
      setPendingDeliveryAddress(null);
      continueToOrderTracking(orderId);
    }
  };

  const handleSkipSaveAddress = () => {
    const orderId = pendingOrderId;
    setSaveAddressDialogOpen(false);
    setPendingOrderId(null);
    setPendingDeliveryAddress(null);
    if (orderId) continueToOrderTracking(orderId);
  };

  const duplicateAddress = address && address.street && address.city && addressMode === "new" && savedAddresses.some(a =>
    a.street?.trim().toLowerCase() === address.street.trim().toLowerCase() &&
    a.city?.trim().toLowerCase() === address.city.trim().toLowerCase()
  );

  if (!orderPlaced && (!token || cart.length === 0)) return null;

  return (
    <div className="p-4 md:p-8 max-w-xl mx-auto relative z-[1]" data-testid="checkout-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <span className="lux-eyebrow">{t("Bästa Biten")}</span>
        <h1 className="font-playfair text-3xl md:text-4xl text-[#F5F1E8] mt-3 mb-6 lux-title">{t("Checkout Title")}</h1>

        {/* Order Type */}
        <div className="lux-surface rounded-xl p-5 mb-4">
          <h3 className="font-medium text-[#EDEDED] mb-3">{t("How would you like your order?")}</h3>
          {!restaurantInfo.accepting_pickup && !restaurantInfo.accepting_delivery && (
            <div className="mb-3 bg-[#DC2626]/10 border border-[#DC2626]/40 text-[#FCA5A5] text-xs font-semibold rounded-lg px-3 py-2">
              {t("Restaurant is temporarily not accepting orders.")}
            </div>
          )}
          <RadioGroup value={orderType} onValueChange={setOrderType} className="grid grid-cols-2 gap-3">
            <Label htmlFor="pickup" className={`flex items-center gap-3 p-4 rounded-lg border transition-all ${restaurantInfo.accepting_pickup ? "cursor-pointer" : "cursor-not-allowed opacity-50"} ${orderType === "pickup" ? "border-[#D4B483] bg-[#D4B483]/10" : "border-[#27272A] bg-[#1A1A1A]"}`}>
              <RadioGroupItem value="pickup" id="pickup" data-testid="radio-pickup" disabled={!restaurantInfo.accepting_pickup} />
              <div>
                <Store className="w-5 h-5 text-[#D4B483] mb-1" />
                <span className="text-sm text-[#EDEDED] font-medium">{t("Pickup")}</span>
                {!restaurantInfo.accepting_pickup && <p className="text-[10px] text-[#FCA5A5]">{t("Temporarily unavailable")}</p>}
              </div>
            </Label>
            <Label htmlFor="delivery" className={`flex items-center gap-3 p-4 rounded-lg border transition-all ${restaurantInfo.accepting_delivery ? "cursor-pointer" : "cursor-not-allowed opacity-50"} ${orderType === "delivery" ? "border-[#D4B483] bg-[#D4B483]/10" : "border-[#27272A] bg-[#1A1A1A]"}`}>
              <RadioGroupItem value="delivery" id="delivery" data-testid="radio-delivery" disabled={!restaurantInfo.accepting_delivery} />
              <div>
                <MapPin className="w-5 h-5 text-[#D4B483] mb-1" />
                <span className="text-sm text-[#EDEDED] font-medium">{t("Delivery")}</span>
                {!restaurantInfo.accepting_delivery && <p className="text-[10px] text-[#FCA5A5]">{t("Temporarily unavailable")}</p>}
              </div>
            </Label>
          </RadioGroup>
        </div>

        {/* Delivery Address */}
        {orderType === "delivery" && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="lux-surface rounded-xl p-5 mb-4">
            <h3 className="font-medium text-[#EDEDED] mb-3">{t("Delivery Address")}</h3>

            {addressMode === null && (
              <div>
                {savedAddresses.length > 0 && (
                  <div className="space-y-2 mb-4">
                    <p className="text-[#D4D4D8] text-xs">{t("Choose a saved address")}</p>
                    {savedAddresses.map((addr) => (
                      <button
                        key={addr.id}
                        type="button"
                        onClick={() => {
                          const display = `${addr.street}, ${addr.city}`;
                          setSelectedSavedAddress(addr);
                          setAddress({ street: addr.street, city: addr.city, display });
                          setAddressMode("saved");
                        }}
                        className="w-full flex items-center gap-3 p-3 rounded-lg border border-[#27272A] bg-[#1A1A1A] hover:border-[#3F3F46] text-left transition-all"
                      >
                        <MapPin className="w-4 h-4 text-[#D4B483] shrink-0" />
                        <div>
                          <p className="text-[#EDEDED] text-sm font-medium">{addr.street}</p>
                          <p className="text-[#D4D4D8] text-xs">{addr.city} <span className="text-[#A8ABB2]">· {addr.label || t("Home")}</span></p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <Button
                  type="button"
                  onClick={() => setAddressMode("new")}
                  variant="outline"
                  className="w-full border-[#27272A] text-[#D4B483] hover:bg-[#D4B483]/10"
                >
                  + {t("Add address")}
                </Button>
              </div>
            )}

            {addressMode === "saved" && selectedSavedAddress && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-sm text-[#D4D4D8]">
                    <MapPin className="w-4 h-4 text-[#10B981]" />
                    <span>{selectedSavedAddress.street}, {selectedSavedAddress.city} <span className="text-[#A8ABB2]">({selectedSavedAddress.label || t("Home")})</span></span>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setAddressMode(null); setAddress(null); setSelectedSavedAddress(null); }}
                    className="text-xs text-[#D4B483] hover:underline"
                  >
                    {t("Change")}
                  </button>
                </div>
                <MapAddressPicker
                  key={`saved-${selectedSavedAddress.id}`}
                  restaurantLat={restaurantInfo.latitude}
                  restaurantLng={restaurantInfo.longitude}
                  restaurantAddress={restaurantInfo.address}
                  initialAddress={{ street: selectedSavedAddress.street, city: selectedSavedAddress.city }}
                  onAddressChange={(addr) => setAddress(addr)}
                />
              </div>
            )}

            {addressMode === "new" && (
              <div>
                <button
                  type="button"
                  onClick={() => { setAddressMode(null); setAddress(null); }}
                  className="text-xs text-[#D4B483] hover:underline mb-3 block"
                >
                  &larr; {t("Back to saved addresses")}
                </button>

                {duplicateAddress && (
                  <div className="bg-[#EF4444]/10 border border-[#EF4444]/40 text-[#FCA5A5] text-xs font-semibold rounded-lg px-3 py-2 mb-3">
                    ❌ {t("You already have this address saved")}
                  </div>
                )}

                <MapAddressPicker
                  key="new-address"
                  restaurantLat={restaurantInfo.latitude}
                  restaurantLng={restaurantInfo.longitude}
                  restaurantAddress={restaurantInfo.address}
                  onAddressChange={(addr) => setAddress(addr)}
                />
              </div>
            )}
          </motion.div>
        )}

        {/* Order Summary */}
        <div className="lux-surface rounded-xl p-5 mb-4">
          <h3 className="font-medium text-[#EDEDED] mb-3">{t("Order Summary")}</h3>
          <div className="space-y-2">
            {(calculatedCart?.items || cart).map((item, i) => (
              <div key={i} className="flex justify-between text-sm gap-3">
                <div>
                  <span className="text-[#D4D4D8]">
                    {item.product_name || item.product?.name} x{item.quantity}
                  </span>
                  {unavailableProductIds.has(item.product_id || item.product?.id) && (
                    <div className="inline-flex items-center mt-1 bg-[#DC2626]/15 border border-[#DC2626]/40 text-[#FCA5A5] text-[10px] font-semibold px-2 py-0.5 rounded-md">
                      {t("Out of stock")}
                    </div>
                  )}
                </div>
                <span className="font-mono-price text-[#EDEDED]">
                  {Number(item.item_total ?? getFallbackItemTotal(item)).toFixed(0)} kr
                </span>
              </div>
            ))}
          </div>
          {hasUnavailableItems && (
            <div className="mt-3 bg-[#DC2626]/10 border border-[#DC2626]/40 text-[#FCA5A5] text-xs font-semibold rounded-lg px-3 py-2">
              {t("Some items are out of stock. Remove them before placing your order.")}
            </div>
          )}
          <Separator className="my-3 bg-[#27272A]" />
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-[#D4D4D8]">{t("Subtotal")}</span>
              <span className="font-mono-price text-[#EDEDED]">{subtotalValue.toFixed(0)} kr</span>
            </div>
            {orderType === "delivery" && (
              <div className="flex justify-between text-sm">
                <span className="text-[#D4D4D8]">{t("Delivery fee")}</span>
                <span className="font-mono-price text-[#EDEDED]">{deliveryFeeValue.toFixed(0)} kr</span>
              </div>
            )}
            {discountValue > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-[#D4D4D8]">{t("Discount")} {calculatedCart?.promo_code ? `(${calculatedCart.promo_code})` : ""}</span>
                <span className="font-mono-price text-[#10B981]">- {discountValue.toFixed(0)} kr</span>
              </div>
            )}
            <div className="flex justify-between font-bold pt-1">
              <span className="text-[#EDEDED]">{t("Total")}</span>
              <span className="font-mono-price text-[#D4B483] text-lg">{totalValue.toFixed(0)} kr</span>
            </div>
            {minOrderAmountValue > 0 && (
              <div className={`text-xs ${meetsMinOrder ? "text-[#10B981]" : "text-[#F59E0B]"}`}>
                {t("Minimum order amount is")}: {minOrderAmountValue.toFixed(0)} kr
              </div>
            )}
          </div>
        </div>

        <div className="lux-surface rounded-xl p-5 mb-4">
          <h3 className="font-medium text-[#EDEDED] mb-2">{t("Promo code")}</h3>
          <div className="flex gap-2">
            <Input
              value={promoCode}
              onChange={e => setPromoCode(e.target.value.toUpperCase())}
              placeholder={t("Enter promo code")}
              className="bg-[#1A1A1A] border-[#27272A] text-[#EDEDED]"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => setPromoAppliedCode((promoCode || "").trim().toUpperCase())}
              className="border-[#D4B483]/40 text-[#D4B483] hover:bg-[#D4B483]/10"
            >
              {t("Apply")}
            </Button>
            {promoAppliedCode && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setPromoAppliedCode("");
                  setPromoCode("");
                }}
                className="border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10"
              >
                {t("Remove promo")}
              </Button>
            )}
          </div>
          {promoAppliedCode && (
            <p className="text-xs text-[#10B981] mt-2">{t("Promo code active")}: {promoAppliedCode}</p>
          )}
        </div>

        {/* Special Instructions */}
        <div className="lux-surface rounded-xl p-5 mb-4">
          <h3 className="font-medium text-[#EDEDED] mb-2">{t("Special Instructions")}</h3>
          <p className="text-[#A8ABB2] text-xs mb-3">{t("Any allergies, preferences, or messages for the kitchen?")}</p>
          <textarea
            value={specialInstructions}
            onChange={e => setSpecialInstructions(e.target.value)}
            placeholder="e.g. No nuts, extra spicy, birthday celebration..."
            rows={3}
            className="w-full rounded-lg border border-[#27272A] bg-[#1A1A1A] text-[#EDEDED] px-3 py-2.5 text-sm placeholder:text-[#3F3F46] focus:outline-none focus:ring-2 focus:ring-[#D4B483]/50 focus:border-[#D4B483] resize-none transition-all"
            data-testid="special-instructions-input"
          />
        </div>

        <div className="flex items-center gap-3 lux-surface rounded-xl px-4 py-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-[#D4B483]/15 flex items-center justify-center flex-shrink-0">
            {orderType === "delivery" ? <MapPin className="w-4 h-4 text-[#D4B483]" /> : <Store className="w-4 h-4 text-[#D4B483]" />}
          </div>
          <div>
            <p className="text-[#EDEDED] text-xs font-semibold">
              {orderType === "delivery" ? t("Payment on delivery") : t("Payment on pickup")}
            </p>
            <p className="text-[#A8ABB2] text-[11px] mt-0.5">
              {orderType === "delivery" ? t("You pay when you receive your delivery") : t("You pay when you pick up your order")}{t("No card payment needed now.")}
            </p>
          </div>
        </div>

        <Button onClick={handlePlaceOrder} disabled={loading || hasUnavailableItems || !meetsMinOrder || !restaurantInfo.is_open || (!restaurantInfo.accepting_pickup && !restaurantInfo.accepting_delivery)} className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold h-12 text-base disabled:opacity-50" data-testid="place-order-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("Placing Order...")}</> : hasUnavailableItems ? t("Remove sold out items") : !meetsMinOrder ? `${t("Min")} ${minOrderAmountValue.toFixed(0)} kr ${t("required")}` : (!restaurantInfo.accepting_pickup && !restaurantInfo.accepting_delivery) ? t("Orders temporarily suspended") : t("Place Order")}
        </Button>
      </motion.div>

      <Dialog
        open={saveAddressDialogOpen}
        onOpenChange={(open) => {
          if (savingAddressAfterOrder) return;
          if (!open) {
            handleSkipSaveAddress();
            return;
          }
          setSaveAddressDialogOpen(true);
        }}
      >
        <DialogContent className="bg-[#121212] border-[#27272A]">
          <DialogHeader>
            <DialogTitle className="text-[#EDEDED] font-playfair">{t("Save address to profile?")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#D4D4D8]">{t("This address is not saved in your profile. Save it now?")}</p>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleSkipSaveAddress}
                disabled={savingAddressAfterOrder}
                className="flex-1 border-[#27272A] text-[#EDEDED] hover:bg-[#1A1A1A]"
              >
                {t("No, continue")}
              </Button>
              <Button
                type="button"
                onClick={handleSaveAddressAfterOrder}
                disabled={savingAddressAfterOrder}
                className="flex-1 bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5]"
              >
                {savingAddressAfterOrder ? <Loader2 className="w-4 h-4 animate-spin" /> : t("Yes, save")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
